import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useUserPermissions } from "../../UserPermissionsContext";

type Agent = {
    id: number;
    name: string;
    sales: number;
    avatar: string;
    rank: number;
};

interface LeaderboardApi {
    id: number;
    username: string;
    sales_count: number;
    photo: string | null;
    sl_no: number;
    latest_sale: string | null;
}

const TABS = [
    { key: "today", label: "Today" },
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "overall", label: "Overall" },
];

const FALLBACK_AVATAR = "/assets/img/logo.png";

export default function Leaderboard() {
    const { webApi } = useUserPermissions();
    const jwtToken = Cookies.get("authToken");

    const [active, setActive] = useState<"today" | "week" | "month" | "overall">(
        "today"
    );
    const [leaderboard, setLeaderBoard] = useState<LeaderboardApi[]>([]);

    const tabToDays: Record<string, string> = {
        today: "today",
        week: "week",
        month: "month",
        overall: "all",
    };

    const options = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    };

    const fetchLeaderboard = async (days: string) => {
        try {
            const response = await fetch(
                `${webApi}/new_dashboards/wizklub_leader_board/?filter=${days}`,
                options
            );
            const data = await response.json();
            setLeaderBoard(data || []);
        } catch (e) {
            console.error("Leaderboard fetch error", e);
            setLeaderBoard([]);
        }
    };

    useEffect(() => {
        fetchLeaderboard(tabToDays[active]);
    }, [active]);

    /** Convert API → UI agents */
    const agents: Agent[] = leaderboard.map((a) => {
        let avatar = FALLBACK_AVATAR;

        if (a.photo && a.photo.trim() !== "") {
            avatar = a.photo.startsWith("http")
                ? a.photo
                : `${webApi}${a.photo}`; // relative → absolute
        }

        return {
            id: a.id,
            name: a.username,
            sales: a.sales_count,
            avatar,
            rank: a.sl_no,
        };
    });

    const top3 = agents
        .filter((a) => a.rank <= 3)
        .sort((a, b) => a.rank - b.rank);

    const others = agents
        .filter((a) => a.rank > 3)
        .sort((a, b) => a.rank - b.rank);

    return (
        <div
            style={{
                padding: 20,
                color: "white",
                fontFamily: "sans-serif",
                minHeight: "100%",
            }}
        >
            {/* TOP 3 */}
            {top3.length >= 3 && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-end",
                        gap: 16,
                        marginBottom: 14,
                    }}
                >
                    <TopBar agent={top3[1]} rank={2} height={120} />
                    <TopBar agent={top3[0]} rank={1} height={160} />
                    <TopBar agent={top3[2]} rank={3} height={110} />
                </div>
            )}

            {/* TABS */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                    marginBottom: 20,
                }}
            >
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setActive(t.key as any)}
                        style={{
                            border: "none",
                            borderRadius: 999,
                            padding: "6px 14px",
                            fontSize: 12,
                            cursor: "pointer",
                            background:
                                active === t.key ? "#5df2c1" : "rgba(255,255,255,0.08)",
                            color: active === t.key ? "#04241a" : "white",
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* OTHERS */}
            <div
                style={{
                    maxHeight: "100vh",
                    overflowY: "auto",
                    paddingRight: 4,
                }}
            >
                {others.map((a) => (
                    <div
                        key={a.id}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: "rgba(255,255,255,0.05)",
                            padding: "10px 12px",
                            borderRadius: 12,
                            marginBottom: 8,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <img
                                src={a.avatar}
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src =
                                        FALLBACK_AVATAR;
                                }}
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                }}
                            />
                            <div>
                                <div style={{ fontSize: 13 }}>{a.name}</div>
                                <div style={{ fontSize: 12, color: "#5df2c1" }}>
                                    {a.sales.toLocaleString()}
                                </div>
                            </div>
                        </div>

                        <div style={{ fontSize: 12, opacity: 0.8 }}>#{a.rank}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TopBar({
    agent,
    rank,
    height,
}: {
    agent: Agent;
    rank: number;
    height: number;
}) {
    const FALLBACK_AVATAR = "/assets/img/logo.png";

    return (
        <div style={{ textAlign: "center", width: 95 }}>
            <div
                style={{
                    width: 95,
                    height,
                    borderRadius: 18,
                    overflow: "hidden",
                    position: "relative",
                    backgroundImage: `url(${agent.avatar || FALLBACK_AVATAR})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.35)",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            rank === 1
                                ? "linear-gradient(180deg,rgba(0,0,0,0.1),rgba(0,0,0,0.6))"
                                : "linear-gradient(180deg,rgba(0,0,0,0.2),rgba(0,0,0,0.7))",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        paddingBottom: 6,
                    }}
                >
                    {/* NAME */}
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            textAlign: "center",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: "14px",
                            padding: "0 6px",
                            maxWidth: "100%",
                        }}
                    >
                        {agent.name}
                    </div>

                    {/* SALES */}

                </div>
            </div>

            <div style={{ fontSize: 13 }}>#{rank}</div>
            <div style={{ fontSize: 12, color: "#5df2c1" }}>
                {agent.sales.toLocaleString()}
            </div>
        </div>
    );
}